import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Search,
  Calendar,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  Users,
  CheckSquare,
} from "lucide-react";

const formatDate = (value) => {
  if (!value || value === "no hay datos" || value === "No hay datos") return value || "Sin fecha";
  try {
    const date = parseISO(value);
    if (isNaN(date.getTime())) return value || "Sin fecha";
    return format(date, "dd-MM-yyyy", { locale: es });
  } catch (error) {
    return value || "Sin fecha";
  }
};

export default function ClientesPage() {
  const [clientes, setClientes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadClientes();
    loadStats();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await api.getClientes();
      setClientes(response.data);
    } catch (error) {
      console.error("Error loading clientes:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.getClientesStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const filteredClientes = clientes.filter((c) =>
    c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ubicacion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-primary font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div data-testid="clientes-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold text-primary">
          Clientes
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestiona tus clientes confirmados
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-0 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary rounded-xl">
                <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Clientes</p>
                <p className="font-heading text-3xl font-bold text-primary">
                  {stats?.total || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-secondary rounded-xl">
                <CheckSquare className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tareas Pendientes</p>
                <p className="font-heading text-3xl font-bold text-primary">
                  {stats?.tareas_pendientes || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="search-clientes-input"
          placeholder="Buscar por nombre, email o ubicación..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-11 rounded-xl"
        />
      </div>

      {/* Clients List */}
      {filteredClientes.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClientes.map((cliente, index) => (
            <Link
              key={cliente.id}
              to={`/clientes/${cliente.id}`}
              data-testid={`cliente-card-${cliente.id}`}
              className={`animate-fade-in stagger-${(index % 5) + 1}`}
            >
              <Card className="border-0 shadow-soft hover:shadow-lg transition-all duration-300 cursor-pointer group h-full">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg text-primary group-hover:text-secondary transition-colors">
                        {cliente.nombre}
                      </h3>
                      <Badge variant="outline" className="mt-1">
                        {cliente.tipo_evento}
                      </Badge>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" strokeWidth={1.5} />
                      <span>{formatDate(cliente.fecha_evento)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" strokeWidth={1.5} />
                      <span className="truncate">{cliente.ubicacion}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" strokeWidth={1.5} />
                      <span>{cliente.telefono}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" strokeWidth={1.5} />
                      <span className="truncate">{cliente.email}</span>
                    </div>
                  </div>
                  {cliente.tipo_pack && (
                    <div className="mt-4 pt-4 border-t border-border/40">
                      <p className="text-xs text-muted-foreground">Pack</p>
                      <p className="text-sm font-medium text-primary">{cliente.tipo_pack}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-soft">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-heading text-xl font-semibold text-primary mb-2">
              {searchTerm ? "Sin resultados" : "No hay clientes"}
            </h3>
            <p className="text-muted-foreground">
              {searchTerm
                ? "Intenta con otro término de búsqueda"
                : "Los clientes aparecerán aquí cuando conviertas una consulta"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
